import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AnalyticsEvent,
  Button,
} from '@cibli/shared';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useI18nDirection } from '@/hooks/useI18nDirection.ts';
import { useTrackEvent } from '@/lib/analytics/use-track-event';

type IdleModalProps = {
  durationInSeconds?: number;
  onContinue: () => void;
  onQuit: () => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  quitLabel?: string;
};

const DEFAULT_DURATION_IN_SECONDS = 60;

const IdleModal = ({
  onContinue,
  onQuit,
  open,
  onOpenChange,
  durationInSeconds: duration = DEFAULT_DURATION_IN_SECONDS,
  quitLabel,
}: IdleModalProps) => {
  const { t } = useTranslation();
  const [countdown, setCountdown] = useState(duration);
  const directionRef = useI18nDirection<HTMLDivElement>();
  const trackEvent = useTrackEvent();
  const hasTriggeredQuitRef = useState<{ current: boolean }>({ current: false })[0];

  const trackIdModalRendered = useCallback(() => {
    trackEvent({ event: AnalyticsEvent.IDLE_MODAL_RENDERED });
  }, [trackEvent]);

  const trackLogoutFromIdleModal = useCallback(() => {
    trackEvent({ event: AnalyticsEvent.LOGOUT_FROM_IDLE_MODAL });
  }, [trackEvent]);

  useEffect(() => {
    if (open) {
      hasTriggeredQuitRef.current = false;
      trackIdModalRendered();
    }
  }, [trackIdModalRendered, open, hasTriggeredQuitRef]);

  useEffect(() => {
    if (!open) {
      setCountdown(duration);
      return;
    }

    // Only reset countdown when modal opens, not on other dependency changes
    setCountdown(duration);

    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          if (!hasTriggeredQuitRef.current) {
            hasTriggeredQuitRef.current = true;
            onOpenChange(false);
            trackLogoutFromIdleModal();
            void onQuit();
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, onQuit, duration, hasTriggeredQuitRef]);

  const handleContinue = useCallback(() => {
    onContinue();
    onOpenChange(false);
  }, [onContinue, onOpenChange]);

  const handleQuit = useCallback(() => {
    onQuit();
    onOpenChange(false);
  }, [onQuit, onOpenChange]);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="sm:min-w-2xl" ref={directionRef}>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-2xl font-bold">{t('IdleModal.title')}</AlertDialogTitle>
          <AlertDialogDescription className="text-lg space-y-2">
            <span className="block">{t('IdleModal.subtitle')}</span>
            <span className="block">{t('IdleModal.description', { seconds: countdown })}</span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button variant="outline" size="lg" onClick={handleQuit}>
            {quitLabel ?? t('IdleModal.quit')}
          </Button>
          <Button size="lg" onClick={handleContinue}>
            {t('IdleModal.continue')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default IdleModal;
